import { useState, useEffect, useRef, useCallback } from 'react';
import { integrationsAPI, channelsAPI, merchantAPI, workflowsAPI } from '../../services/api';
import { useSearchParams } from 'react-router-dom';
import { Plus, Trash2, RefreshCw, CheckCircle, ExternalLink, X, Upload, FileText, File, Settings, ArrowRight, Bot, Users, ChevronDown, ChevronUp, LayoutGrid, List, Lock, Database, Pencil, Table2, Copy, Check, Download, Clock } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

// ============ Icons ============
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
);
const SheetsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M5 3a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V8l-6-5H5z" fill="#0F9D58"/><path d="M15 3l6 5h-6V3z" fill="#87CEAC"/><rect x="7" y="11" width="10" height="1" fill="#fff"/><rect x="7" y="13.5" width="10" height="1" fill="#fff"/><rect x="7" y="16" width="6" height="1" fill="#fff"/><rect x="11.5" y="11" width="1" height="6" fill="#fff"/></svg>
);
const CalendarIconSvg = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="18" height="18" rx="3" fill="#4285F4"/><rect x="5" y="8" width="14" height="12" rx="1" fill="#fff"/><rect x="5" y="3" width="14" height="5" rx="1" fill="#1A73E8"/><rect x="7" y="2" width="2" height="3" rx="0.5" fill="#1A73E8"/><rect x="15" y="2" width="2" height="3" rx="0.5" fill="#1A73E8"/><rect x="7.5" y="11" width="3" height="2" rx="0.3" fill="#4285F4"/><rect x="12.5" y="11" width="3" height="2" rx="0.3" fill="#4285F4"/></svg>
);
const DriveIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M8.5 2L1 15h6.5L15 2H8.5z" fill="#0066DA"/><path d="M15 2l-7.5 13H23L15.5 2H15z" fill="#00AC47"/><path d="M1 15l3.5 6h15l3.5-6H1z" fill="#FFBA00"/></svg>
);

// ============ Database Types ============
const DB_TYPES = [
  { value: 'mysql', label: 'MySQL', color: '#4479A1' },
  { value: 'postgresql', label: 'PostgreSQL', color: '#336791' },
  { value: 'mssql', label: 'SQL Server', color: '#CC2927' },
  { value: 'mongodb', label: 'MongoDB', color: '#47A248' },
  { value: 'sqlite', label: 'SQLite', color: '#003B57' },
];
const DB_PORTS = { mysql: 3306, postgresql: 5432, mssql: 1433, mongodb: 27017, sqlite: null };

const DbIcon = ({ type, size = 18 }) => {
  const color = { mysql: '#4479A1', postgresql: '#336791', mssql: '#CC2927', mongodb: '#47A248', sqlite: '#003B57' }[type] || '#64748b';
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <ellipse cx="12" cy="6.5" rx="8" ry="2.8" fill={color} />
      <path d="M4 6.5v5c0 1.55 3.58 2.8 8 2.8s8-1.25 8-2.8v-5" stroke={color} strokeWidth="1.5" fill="none" />
      <path d="M4 11.5v5c0 1.55 3.58 2.8 8 2.8s8-1.25 8-2.8v-5" stroke={color} strokeWidth="1.5" fill="none" />
    </svg>
  );
};

// ============ Helpers ============
const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};
const getFileTypeBadge = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  return { pdf: 'PDF', docx: 'DOCX', doc: 'DOC', xlsx: 'XLSX', xls: 'XLS', csv: 'CSV', txt: 'TXT' }[ext] || ext?.toUpperCase() || 'FILE';
};

// ============ n8n-style Node ============
const nodeStyle = (connected) => ({
  position: 'relative',
  borderRadius: 12,
  border: `2px solid ${connected ? '#22c55e' : '#e2e8f0'}`,
  background: '#fff',
  boxShadow: connected ? '0 0 0 3px rgba(34,197,94,0.1)' : '0 1px 3px rgba(0,0,0,0.06)',
  transition: 'all 0.2s',
});

const nodeHeaderStyle = (clickable) => ({
  padding: '14px 16px',
  display: 'flex', alignItems: 'center', gap: 12,
  cursor: clickable ? 'pointer' : 'default',
  userSelect: 'none',
});

const nodeIconBox = (connected) => ({
  width: 40, height: 40, borderRadius: 10, flexShrink: 0,
  background: connected ? '#f0fdf4' : '#f8fafc',
  border: `1.5px solid ${connected ? '#22c55e' : '#e2e8f0'}`,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
});

const dotStyle = (side, connected) => ({
  position: 'absolute',
  [side]: -7,
  top: '50%',
  transform: 'translateY(-50%)',
  width: 12, height: 12,
  borderRadius: '50%',
  background: connected ? '#22c55e' : '#e2e8f0',
  border: '2px solid #fff',
  boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
});

const connectorLine = {
  width: 2, background: '#e2e8f0', margin: '0 auto', height: 24, flexShrink: 0,
};

// ============ Upload File Modal ============
function UploadFileModal({ onClose, onUploaded }) {
  const [file, setFile] = useState(null);
  const [purpose, setPurpose] = useState('knowledge_base');
  const [aiInstructions, setAiInstructions] = useState('');
  const [uploading, setUploading] = useState(false);
  const purposes = [
    { value: 'knowledge_base', label: 'Knowledge Base' }, { value: 'price_list', label: 'Price List' },
    { value: 'product_catalog', label: 'Product Catalog' }, { value: 'faq', label: 'FAQ' },
    { value: 'menu', label: 'Menu' }, { value: 'policy', label: 'Policy' }, { value: 'custom', label: 'Custom' },
  ];
  const handleUpload = async () => {
    if (!file) return toast.error('Select a file');
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('purpose', purpose); fd.append('aiInstructions', aiInstructions);
      await integrationsAPI.uploadFile(fd); toast.success('File uploaded'); onUploaded(); onClose();
    } catch (_) {} finally { setUploading(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><span className="modal-title">Upload File</span><button className="modal-close" onClick={onClose}><X size={18} /></button></div>
        <div className="form-group"><label className="form-label">File</label><input type="file" className="form-input" accept=".pdf,.docx,.xlsx,.csv,.txt" onChange={e => setFile(e.target.files[0])} /><div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 4 }}>PDF, DOCX, XLSX, CSV, TXT</div></div>
        <div className="form-group"><label className="form-label">Purpose</label><select className="form-input" value={purpose} onChange={e => setPurpose(e.target.value)}>{purposes.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
        <div className="form-group"><label className="form-label">AI Instructions (optional)</label><textarea className="form-input" rows={2} value={aiInstructions} onChange={e => setAiInstructions(e.target.value)} placeholder="e.g. Use this when customers ask about return policies." style={{ fontSize: '0.82rem' }} /></div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={handleUpload} disabled={uploading}><Upload size={14} /> {uploading ? 'Uploading...' : 'Upload'}</button></div>
      </div>
    </div>
  );
}

// ============ Drive Picker ============
function useDrivePicker({ onFilesPicked }) {
  const [loading, setLoading] = useState(false);
  const [pickerReady, setPickerReady] = useState(false);
  useEffect(() => {
    if (document.getElementById('google-picker-script')) { setPickerReady(true); return; }
    const s = document.createElement('script'); s.id = 'google-picker-script'; s.src = 'https://apis.google.com/js/api.js';
    s.onload = () => { window.gapi.load('picker', () => setPickerReady(true)); }; document.body.appendChild(s);
  }, []);
  const openPicker = async () => {
    if (!pickerReady) return toast.error('Loading...');
    setLoading(true);
    try {
      const { data } = await integrationsAPI.getPickerToken();
      const { accessToken, developerKey } = data.data;
      const p = new window.google.picker.PickerBuilder().setOAuthToken(accessToken)
        .addView(new window.google.picker.DocsView().setIncludeFolders(true).setMimeTypes('application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain,application/vnd.google-apps.document,application/vnd.google-apps.spreadsheet'))
        .setCallback(r => { if (r.action === window.google.picker.Action.PICKED) onFilesPicked(r.docs); })
        .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED).setTitle('Select files for AI Knowledge Base').build();
      if (developerKey) p.setRelayUrl(undefined); p.setVisible(true);
    } catch (_) { toast.error('Failed to open picker'); } finally { setLoading(false); }
  };
  return { openPicker, loading };
}

// ============ Drive Import Modal ============
function DriveImportModal({ pickedFiles, onClose, onImported }) {
  const [purpose, setPurpose] = useState('knowledge_base');
  const [importing, setImporting] = useState({});
  const [done, setDone] = useState({});
  const purposes = [{ value: 'knowledge_base', label: 'Knowledge Base' }, { value: 'price_list', label: 'Price List' }, { value: 'product_catalog', label: 'Product Catalog' }, { value: 'faq', label: 'FAQ' }, { value: 'menu', label: 'Menu' }, { value: 'custom', label: 'Custom' }];
  const doImport = async (id) => { setImporting(p => ({ ...p, [id]: true })); try { await integrationsAPI.importDriveFile(id, { purpose }); setDone(p => ({ ...p, [id]: true })); toast.success('Imported'); } catch (_) {} finally { setImporting(p => ({ ...p, [id]: false })); } };
  const doAll = async () => { for (const f of pickedFiles) { if (!done[f.id]) await doImport(f.id); } onImported(); onClose(); };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 540 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><span className="modal-title">Import {pickedFiles.length} file{pickedFiles.length > 1 ? 's' : ''}</span><button className="modal-close" onClick={onClose}><X size={18} /></button></div>
        <div className="form-group"><label className="form-label">Purpose</label><select className="form-input" value={purpose} onChange={e => setPurpose(e.target.value)}>{purposes.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
          {pickedFiles.map(f => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: done[f.id] ? '#f0fdf4' : '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
              <File size={15} style={{ color: '#94a3b8', flexShrink: 0 }} />
              <div style={{ flex: 1, fontSize: '0.82rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
              {done[f.id] ? <CheckCircle size={15} style={{ color: '#22c55e' }} /> : <button className="btn btn-primary btn-sm" disabled={importing[f.id]} onClick={() => doImport(f.id)}>{importing[f.id] ? '...' : 'Import'}</button>}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>{pickedFiles.every(f => done[f.id]) ? 'Done' : 'Cancel'}</button>
          {!pickedFiles.every(f => done[f.id]) && <button className="btn btn-primary" onClick={doAll}><ArrowRight size={14} /> Import All</button>}
        </div>
      </div>
    </div>
  );
}

// ============ Database Connect / Edit Modal ============
function DatabaseConnectModal({ onClose, onConnected, editingDb }) {
  const isEdit = !!editingDb;
  const [form, setForm] = useState(() => isEdit
    ? { type: editingDb.type, name: editingDb.name, host: editingDb.host || 'localhost', port: editingDb.port || DB_PORTS[editingDb.type] || '', database: editingDb.database || '', username: editingDb.username || '', password: '', ssl: editingDb.ssl || false, aiInstructions: editingDb.aiInstructions || '' }
    : { type: 'postgresql', name: '', host: '', port: 5432, database: '', username: '', password: '', ssl: true, aiInstructions: '' }
  );
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const changeType = (type) => {
    if (isEdit) return; // don't allow type change when editing
    setTestResult(null);
    const defaultSsl = ['postgresql', 'mssql'].includes(type);
    setForm(p => ({ ...p, type, port: DB_PORTS[type] || '', ssl: defaultSsl, host: type === 'sqlite' ? '' : p.host }));
  };

  const handleTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      await integrationsAPI.testDatabase(form);
      setTestResult({ ok: true, msg: 'Connection successful!' });
    } catch (e) {
      setTestResult({ ok: false, msg: e?.response?.data?.message || 'Connection failed. Check your credentials.' });
    } finally { setTesting(false); }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Enter a connection name');
    if (form.type !== 'sqlite' && form.type !== 'mongodb' && !form.host.trim()) return toast.error('Enter host');
    if (!form.database.trim()) return toast.error(form.type === 'mongodb' ? 'Enter connection URI' : 'Enter database name');
    setSaving(true);
    try {
      if (isEdit) {
        await integrationsAPI.updateDatabase(editingDb._id, form);
        toast.success('Database updated');
      } else {
        await integrationsAPI.connectDatabase(form);
        toast.success('Database connected');
      }
      onConnected();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || (isEdit ? 'Failed to update' : 'Failed to connect database'));
    } finally { setSaving(false); }
  };

  const isSQLite = form.type === 'sqlite';
  const isMongo = form.type === 'mongodb';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">{isEdit ? `Edit — ${editingDb.name}` : 'Connect Database'}</span>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        {/* DB type selector */}
        <div className="form-group">
          <label className="form-label">Database Type</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {DB_TYPES.map(db => (
              <button key={db.value} onClick={() => changeType(db.value)}
                style={{
                  padding: '7px 14px', borderRadius: 8,
                  border: `2px solid ${form.type === db.value ? db.color : '#e2e8f0'}`,
                  background: form.type === db.value ? `${db.color}18` : '#f8fafc',
                  color: form.type === db.value ? db.color : '#64748b',
                  fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                <DbIcon type={db.value} size={14} /> {db.label}
              </button>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Connection Name</label>
          <input className="form-input" placeholder="e.g. Products DB, Main Database" value={form.name} onChange={e => set('name', e.target.value)} />
        </div>

        {!isSQLite && !isMongo && (
          <div style={{ display: 'flex', gap: 10 }}>
            <div className="form-group" style={{ flex: 2 }}>
              <label className="form-label">Host</label>
              <input className="form-input" placeholder="localhost or IP address" value={form.host} onChange={e => set('host', e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Port</label>
              <input className="form-input" type="number" value={form.port} onChange={e => set('port', parseInt(e.target.value) || '')} />
            </div>
          </div>
        )}

        {isMongo ? (
          <div className="form-group">
            <label className="form-label">Connection URI</label>
            <input className="form-input" placeholder="mongodb://username:password@host:27017/dbname" value={form.database} onChange={e => set('database', e.target.value)} />
            <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 4 }}>Paste your full MongoDB connection string</div>
          </div>
        ) : isSQLite ? (
          <div className="form-group">
            <label className="form-label">Database File Path</label>
            <input className="form-input" placeholder="/var/data/myapp.db" value={form.database} onChange={e => set('database', e.target.value)} />
          </div>
        ) : (
          <div className="form-group">
            <label className="form-label">Database Name</label>
            <input className="form-input" placeholder="myapp_production" value={form.database} onChange={e => set('database', e.target.value)} />
          </div>
        )}

        {!isSQLite && !isMongo && (
          <div style={{ display: 'flex', gap: 10 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Username</label>
              <input className="form-input" placeholder="root" value={form.username} onChange={e => set('username', e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Password</label>
              <input className="form-input" type="password" placeholder="••••••••" value={form.password} onChange={e => set('password', e.target.value)} />
            </div>
          </div>
        )}

        {!isSQLite && !isMongo && (
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.82rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.ssl} onChange={e => set('ssl', e.target.checked)} />
              Enable SSL / TLS
            </label>
          </div>
        )}

        <div className="form-group">
          <label className="form-label">AI Instructions (optional)</label>
          <textarea className="form-input" rows={2} value={form.aiInstructions} onChange={e => set('aiInstructions', e.target.value)} placeholder="e.g. Use this to answer questions about product inventory and stock levels." style={{ fontSize: '0.82rem' }} />
        </div>

        {testResult && (
          <div style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, background: testResult.ok ? '#f0fdf4' : '#fef2f2', border: `1px solid ${testResult.ok ? '#22c55e' : '#ef4444'}`, fontSize: '0.82rem', color: testResult.ok ? '#15803d' : '#dc2626' }}>
            {testResult.ok ? <CheckCircle size={14} /> : <X size={14} />}
            {testResult.msg}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-secondary" onClick={handleTest} disabled={testing}>
            <RefreshCw size={13} style={testing ? { animation: 'spin 0.7s linear infinite' } : {}} />
            {testing ? 'Testing...' : 'Test Connection'}
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <Database size={14} /> {saving ? (isEdit ? 'Saving...' : 'Connecting...') : (isEdit ? 'Save Changes' : 'Connect')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ Database Tables Modal ============
function DbTablesModal({ db, onClose }) {
  const [tables, setTables] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState('');

  useEffect(() => {
    integrationsAPI.getDatabaseTables(db._id)
      .then(({ data }) => setTables(data.data || []))
      .catch(e => setError(e?.response?.data?.message || 'Failed to load tables'))
      .finally(() => setLoading(false));
  }, [db._id]);

  const copy = (name) => {
    navigator.clipboard.writeText(name);
    setCopied(name);
    setTimeout(() => setCopied(''), 1500);
  };

  const dbType = DB_TYPES.find(t => t.value === db.type);
  const itemLabel = db.type === 'mongodb' ? 'collection' : 'table';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <DbIcon type={db.type} size={16} /> {db.name} — Tables
          </span>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>

        <div style={{ padding: '0 4px 4px', fontSize: '0.78rem', color: '#64748b', marginBottom: 8 }}>
          Click any {itemLabel} name to copy it — useful for writing AI instructions.
        </div>

        {loading && <div className="flex-center" style={{ padding: 40 }}><div className="spinner" /></div>}
        {error && <div style={{ padding: 16, color: '#ef4444', fontSize: '0.82rem' }}>{error}</div>}

        {tables && tables.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: '0.82rem' }}>No tables found.</div>
        )}

        {tables && tables.length > 0 && (
          <div style={{ maxHeight: 360, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {tables.map(t => (
              <div key={t.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'pointer' }}
                onClick={() => copy(t.name)}>
                <Table2 size={14} style={{ color: dbType?.color || '#64748b', flexShrink: 0 }} />
                <span style={{ flex: 1, fontWeight: 600, fontSize: '0.83rem', fontFamily: 'monospace' }}>{t.name}</span>
                <span style={{ fontSize: '0.66rem', padding: '1px 6px', borderRadius: 4, fontWeight: 600,
                  background: t.type === 'collection' ? '#f0fdf4' : t.type === 'view' ? '#eff6ff' : '#f8fafc',
                  color: t.type === 'collection' ? '#16a34a' : t.type === 'view' ? '#2563eb' : '#64748b',
                  border: '1px solid #e2e8f0' }}>{t.type}</span>
                {copied === t.name
                  ? <Check size={13} style={{ color: '#22c55e', flexShrink: 0 }} />
                  : <Copy size={13} style={{ color: '#94a3b8', flexShrink: 0 }} />}
              </div>
            ))}
          </div>
        )}

        {tables && (
          <div style={{ marginTop: 12, padding: '10px 12px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, fontSize: '0.75rem', color: '#92400e' }}>
            <strong>Tip:</strong> In your AI Instructions, reference these {itemLabel}s by name so the AI knows which ones to query. Example: <em>"Use the <code>orders</code> table to answer questions about order status."</em>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ MAIN PAGE ============
export default function IntegrationsPage() {
  const { merchant } = useAuth();
  const [searchParams] = useSearchParams();
  const [googleStatus, setGoogleStatus] = useState(null);
  const [connectedSheets, setConnectedSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [spreadsheets, setSpreadsheets] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [preview, setPreview] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [sheetForm, setSheetForm] = useState({ spreadsheetId: '', spreadsheetName: '', sheetName: '', purpose: 'price_list', purposeLabel: '', aiInstructions: '', columnMapping: {} });
  const [editingInstructions, setEditingInstructions] = useState(null);
  const [instructionsText, setInstructionsText] = useState('');
  const [files, setFiles] = useState([]);
  const [showUploadFile, setShowUploadFile] = useState(false);
  const [editingFileInstructions, setEditingFileInstructions] = useState(null);
  const [fileInstructionsText, setFileInstructionsText] = useState('');
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [connectingCalendar, setConnectingCalendar] = useState(false);
  const [connectedChannels, setConnectedChannels] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null); // null = show all
  const [showCreateWorkflow, setShowCreateWorkflow] = useState(false);
  const [workflowForm, setWorkflowForm] = useState({ name: '', description: '', prompt: { name: '', content: '' }, dataSources: { sheetIds: [], knowledgeFileIds: [], useCalendar: false } });
  const [pickedFiles, setPickedFiles] = useState([]);
  const { openPicker, loading: pickerLoading } = useDrivePicker({ onFilesPicked: docs => setPickedFiles(docs) });
  const [dbConnections, setDbConnections] = useState([]);
  const [showAddDB, setShowAddDB] = useState(false);
  const [editingDb, setEditingDb] = useState(null);
  const [showTablesDb, setShowTablesDb] = useState(null);

  // View toggle & sections
  const [view, setView] = useState('canvas');
  const [open, setOpen] = useState({ sheets: true, files: true, databases: true });
  const [canvasTheme, setCanvasTheme] = useState('dark'); // 'dark' or 'light'
  const isDark = canvasTheme === 'dark';
  const [sheetsExpanded, setSheetsExpanded] = useState(false); // n8n-style expand for 2+ sheets on canvas
  const [filesExpanded, setFilesExpanded] = useState(false); // n8n-style expand for 2+ files on canvas
  const [selectedNode, setSelectedNode] = useState(null); // { type: 'sheet'|'file'|'add_sheet'|'add_file', data? }
  const [nodePanelInstructions, setNodePanelInstructions] = useState('');
  const [nodePanelEditingInstr, setNodePanelEditingInstr] = useState(false);

  // Theme colors
  const ct = isDark ? {
    bg: '#1a1a2e', dot: '#2a2a4a', nodeBg: '#16162a', centerBg: '#2d1b69',
    border: '#2a2a4a', borderGreen: '#22c55e', borderPurple: '#7c3aed',
    text: '#e2e8f0', textMuted: '#94a3b8', textDim: '#64748b', sectionLabel: '#4a4a6a',
    lineOff: '#3a3a5a', dotBorder: '#1a1a2e', iconBox: '#1e1e3a', iconBoxBorder: '#2a2a4a',
    centerIconBox: '#3b1f8a', shadowNode: '0 2px 8px rgba(0,0,0,0.3)',
    shadowCenter: '0 0 24px rgba(124,58,237,0.3), 0 0 0 1px rgba(124,58,237,0.1)',
    shadowGreen: '0 0 16px rgba(34,197,94,0.15)', badgeBg: '#16162a', badgeBorder: '#2a2a4a',
  } : {
    bg: '#e8ecf1', dot: '#d1d5db', nodeBg: '#f1f5f9', centerBg: '#ece9fe',
    border: '#cbd5e1', borderGreen: '#22c55e', borderPurple: '#7c3aed',
    text: '#1e293b', textMuted: '#475569', textDim: '#64748b', sectionLabel: '#64748b',
    lineOff: '#94a3b8', dotBorder: '#e8ecf1', iconBox: '#e2e8f0', iconBoxBorder: '#cbd5e1',
    centerIconBox: '#ddd6fe', shadowNode: '0 1px 4px rgba(0,0,0,0.08)',
    shadowCenter: '0 0 20px rgba(124,58,237,0.15), 0 0 0 1px rgba(124,58,237,0.1)',
    shadowGreen: '0 0 12px rgba(34,197,94,0.12)', badgeBg: '#f1f5f9', badgeBorder: '#cbd5e1',
  };

  // Canvas pan & zoom
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStart = useRef({ x: 0, y: 0 });
  const canvasRef = useRef(null);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.08 : 0.08;
    setZoom(z => Math.min(2, Math.max(0.3, z + delta)));
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('button') || e.target.closest('.canvas-node')) return;
    setIsPanning(true);
    panStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  }, [pan]);

  const handleMouseMove = useCallback((e) => {
    if (!isPanning) return;
    setPan({ x: e.clientX - panStart.current.x, y: e.clientY - panStart.current.y });
  }, [isPanning]);

  const handleMouseUp = useCallback(() => setIsPanning(false), []);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const resetView = useCallback(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, []);
  const zoomIn = useCallback(() => setZoom(z => Math.min(2, z + 0.15)), []);
  const zoomOut = useCallback(() => setZoom(z => Math.max(0.3, z - 0.15)), []);
  const tog = k => setOpen(p => ({ ...p, [k]: !p[k] }));

  useEffect(() => {
    if (searchParams.get('google') === 'connected') toast.success('Google connected!');
    if (searchParams.get('google') === 'error') toast.error('Failed to connect');
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [s, sh, f, c, ch, pr, wf, db] = await Promise.all([
        integrationsAPI.getGoogleStatus(), integrationsAPI.getConnectedSheets(),
        integrationsAPI.getFiles().catch(() => ({ data: { data: [] } })),
        integrationsAPI.getCalendarStatus().catch(() => ({ data: { data: null } })),
        channelsAPI.list().catch(() => ({ data: { data: [] } })),
        merchantAPI.getPrompts().catch(() => ({ data: { data: [] } })),
        workflowsAPI.list().catch(() => ({ data: { data: [] } })),
        integrationsAPI.getDatabases().catch(() => ({ data: { data: [] } })),
      ]);
      setGoogleStatus(s.data.data); setConnectedSheets(sh.data.data); setFiles(f.data.data || []); setCalendarStatus(c.data.data);
      setConnectedChannels((ch.data.data || []).filter(c => c.status === 'connected'));
      setPrompts(pr.data.data || []);
      setWorkflows(wf.data.data || []);
      setDbConnections(db.data.data || []);
    } catch (_) {} finally { setLoading(false); }
  };
  const fetchFiles = async () => { try { const { data } = await integrationsAPI.getFiles(); setFiles(data.data || []); } catch (_) {} };

  // Google
  const connectGoogle = async () => { try { const { data } = await integrationsAPI.getGoogleAuthUrl(); window.location.href = data.data.url; } catch (_) {} };
  const disconnectGoogle = async () => { if (!window.confirm('Disconnect Google?')) return; try { await integrationsAPI.disconnectGoogle(); setGoogleStatus(null); setConnectedSheets([]); setCalendarStatus(null); toast.success('Disconnected'); } catch (_) {} };

  // Sheets
  const loadSpreadsheets = async () => { try { const { data } = await integrationsAPI.listSpreadsheets(); setSpreadsheets(data.data); } catch (_) {} };
  const loadTabs = async (id, name) => { try { const { data } = await integrationsAPI.listSheetTabs(id); setTabs(data.data.tabs); setSheetForm(p => ({ ...p, spreadsheetId: id, spreadsheetName: name })); } catch (_) {} };
  const loadPreview = async (id, name) => { try { const { data } = await integrationsAPI.previewSheet({ spreadsheetId: id, sheetName: name }); setPreview(data.data); setSheetForm(p => ({ ...p, sheetName: name })); } catch (_) {} };
  const connectSheet = async () => { try { await integrationsAPI.connectSheet(sheetForm); await fetchData(); setShowAddSheet(false); setPreview(null); setTabs([]); setSheetForm({ spreadsheetId: '', spreadsheetName: '', sheetName: '', purpose: 'price_list', purposeLabel: '', aiInstructions: '', columnMapping: {} }); toast.success('Sheet connected!'); } catch (_) {} };
  const disconnectSheet = async (id) => { try { await integrationsAPI.disconnectSheet(id); setConnectedSheets(p => p.filter(s => s._id !== id)); toast.success('Removed'); } catch (_) {} };
  const syncSheet = async (id) => { setSyncing(id); try { const { data } = await integrationsAPI.syncSheet(id); setConnectedSheets(p => p.map(s => s._id === id ? { ...s, lastSynced: new Date().toISOString(), rowCount: data.data.rowCount } : s)); toast.success(`Synced ${data.data.rowCount} rows`); } catch (_) {} finally { setSyncing(null); } };
  const saveInstructions = async (id) => { try { await integrationsAPI.updateSheetInstructions(id, instructionsText); setConnectedSheets(p => p.map(s => s._id === id ? { ...s, aiInstructions: instructionsText } : s)); setEditingInstructions(null); toast.success('Saved'); } catch (_) {} };
  const savePanelSheetInstructions = async (id, text) => { try { await integrationsAPI.updateSheetInstructions(id, text); setConnectedSheets(p => p.map(s => s._id === id ? { ...s, aiInstructions: text } : s)); setNodePanelEditingInstr(false); toast.success('Saved'); } catch (_) {} };
  const savePanelFileInstructions = async (id, text) => { try { await integrationsAPI.updateFile(id, { aiInstructions: text }); setFiles(p => p.map(f => f._id === id ? { ...f, aiInstructions: text } : f)); setNodePanelEditingInstr(false); toast.success('Saved'); } catch (_) {} };

  // Files
  const deleteFile = async (id) => { if (!window.confirm('Delete?')) return; try { await integrationsAPI.deleteFile(id); setFiles(p => p.filter(f => f._id !== id)); toast.success('Deleted'); } catch (_) {} };

  const downloadFile = async (file) => {
    try {
      const res = await integrationsAPI.downloadFile(file._id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = file.originalName || file.name || 'document';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      if (e.response?.status === 404) toast.error('Original file no longer available');
    }
  };

  const formatUploadedAt = (d) => {
    if (!d) return '';
    try {
      return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Kuala_Lumpur',
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
      }).format(new Date(d)).replace(',', '');
    } catch (_) { return new Date(d).toLocaleString(); }
  };
  const saveFileInstructions = async (id) => { try { await integrationsAPI.updateFile(id, { aiInstructions: fileInstructionsText }); setFiles(p => p.map(f => f._id === id ? { ...f, aiInstructions: fileInstructionsText } : f)); setEditingFileInstructions(null); toast.success('Saved'); } catch (_) {} };

  // Calendar
  const handleConnectCalendar = async () => { setConnectingCalendar(true); try { const { data } = await integrationsAPI.connectCalendar({}); setCalendarStatus(data.data); toast.success('Connected'); } catch (_) {} finally { setConnectingCalendar(false); } };
  const handleDisconnectCalendar = async () => { if (!window.confirm('Disconnect Calendar?')) return; try { await integrationsAPI.disconnectCalendar(); setCalendarStatus(null); toast.success('Disconnected'); } catch (_) {} };

  // Databases
  const fetchDatabases = async () => { try { const { data } = await integrationsAPI.getDatabases(); setDbConnections(data.data || []); } catch (_) {} };
  const handleDisconnectDB = async (id) => { if (!window.confirm('Disconnect this database?')) return; try { await integrationsAPI.disconnectDatabase(id); setDbConnections(p => p.filter(d => d._id !== id)); toast.success('Database disconnected'); } catch (_) {} };
  const handleSaveDBInstructions = async (id, text) => { try { await integrationsAPI.updateDatabaseInstructions(id, text); setDbConnections(p => p.map(d => d._id === id ? { ...d, aiInstructions: text } : d)); setNodePanelEditingInstr(false); toast.success('Saved'); } catch (_) {} };

  const purposeOptions = [{ value: 'price_list', label: 'Price List' }, { value: 'product_catalog', label: 'Product Catalog' }, { value: 'faq', label: 'FAQ' }, { value: 'inventory', label: 'Inventory' }, { value: 'custom', label: 'Custom' }];

  if (loading) return <div className="page-body flex-center" style={{ height: '50vh' }}><div className="spinner" /></div>;

  const isGoogle = !!googleStatus;
  const totalSources = connectedSheets.length + files.length + (calendarStatus?.isActive ? 1 : 0);

  // Workflow CRUD
  const handleCreateWorkflow = async () => {
    if (!workflowForm.name.trim()) return toast.error('Workflow name is required');
    if (!workflowForm.prompt.content.trim()) return toast.error('Prompt is required');
    try {
      const { data } = await workflowsAPI.create(workflowForm);
      setWorkflows(prev => [data.data, ...prev]);
      setShowCreateWorkflow(false);
      setWorkflowForm({ name: '', description: '', prompt: { name: '', content: '' }, dataSources: { sheetIds: [], knowledgeFileIds: [], useCalendar: false } });
      setSelectedWorkflow(data.data._id);
      toast.success('Workflow created');
    } catch (_) {}
  };

  const handleDeleteWorkflow = async (id) => {
    if (!window.confirm('Delete this workflow? Channels will be unassigned.')) return;
    try {
      await workflowsAPI.delete(id);
      setWorkflows(prev => prev.filter(w => w._id !== id));
      if (selectedWorkflow === id) setSelectedWorkflow(null);
      fetchData();
      toast.success('Workflow deleted');
    } catch (_) {}
  };

  const handleAssignChannelToWorkflow = async (workflowId, channelId) => {
    try {
      await workflowsAPI.assignChannel(workflowId, channelId);
      fetchData();
      toast.success('Channel assigned');
    } catch (_) {}
  };

  const handleRemoveChannelFromWorkflow = async (workflowId, channelId) => {
    try {
      await workflowsAPI.removeChannel(workflowId, channelId);
      fetchData();
      toast.success('Channel removed');
    } catch (_) {}
  };

  const handleUpdateWorkflow = async (id, updates) => {
    try {
      const { data } = await workflowsAPI.update(id, updates);
      setWorkflows(prev => prev.map(w => w._id === id ? data.data : w));
      toast.success('Workflow updated');
    } catch (_) {}
  };

  // Get current workflow data for canvas
  const activeWf = selectedWorkflow ? workflows.find(w => w._id === selectedWorkflow) : null;

  // Channels for current view
  const wfChannels = activeWf
    ? connectedChannels.filter(c => c.workflowId && c.workflowId.toString() === activeWf._id.toString())
    : connectedChannels;

  // Data sources for current view
  const wfSheetIds = (activeWf?.dataSources?.sheetIds || []).map(id => id?.toString());
  const wfFileIds = (activeWf?.dataSources?.knowledgeFileIds || []).map(id => id?.toString());
  const wfSheets = wfSheetIds.length > 0
    ? connectedSheets.filter(s => wfSheetIds.includes(s._id?.toString()))
    : activeWf ? [] : connectedSheets;
  const wfFiles = wfFileIds.length > 0
    ? files.filter(f => wfFileIds.includes(f._id?.toString()))
    : activeWf ? [] : files;
  const wfCalendar = activeWf ? !!activeWf.dataSources?.useCalendar : !!calendarStatus?.isActive;
  const wfTotalSources = wfSheets.length + wfFiles.length + (wfCalendar ? 1 : 0);

  // Build data source nodes — individual sheets with n8n-style expand
  const leftNodes = [];
  const currentSheets = activeWf ? wfSheets : connectedSheets;
  const currentFiles = activeWf ? wfFiles : files;
  const currentCalendar = activeWf ? wfCalendar : !!calendarStatus?.isActive;

  const openNodePanel = (type, data = null) => {
    setSelectedNode({ type, data });
    setNodePanelInstructions(data?.aiInstructions || '');
    setNodePanelEditingInstr(false);
  };

  if (currentSheets.length === 1) {
    leftNodes.push({ id: `sheet-${currentSheets[0]._id}`, label: currentSheets[0].sheetName || 'Sheet', sub: currentSheets[0].spreadsheetName || 'Google Sheets', icon: <SheetsIcon />, connected: true, action: () => openNodePanel('sheet', currentSheets[0]), actionLabel: 'Manage →' });
  } else if (currentSheets.length > 1) {
    leftNodes.push({ id: `sheet-${currentSheets[0]._id}`, label: currentSheets[0].sheetName || 'Sheet', sub: currentSheets[0].spreadsheetName || 'Google Sheets', icon: <SheetsIcon />, connected: true, action: () => openNodePanel('sheet', currentSheets[0]), actionLabel: 'Manage →' });
    if (sheetsExpanded) {
      currentSheets.slice(1).forEach((s, i) => {
        leftNodes.push({ id: `sheet-${s._id}`, label: s.sheetName || 'Sheet', sub: s.spreadsheetName || 'Google Sheets', icon: <SheetsIcon />, connected: true, isExpandedChild: true, _isFirstChild: i === 0, _collapseAction: () => setSheetsExpanded(false), action: () => openNodePanel('sheet', s), actionLabel: 'Manage →' });
      });
    } else {
      leftNodes.push({ id: 'sheets-more', label: `+${currentSheets.length - 1} more sheet${currentSheets.length - 1 > 1 ? 's' : ''}`, sub: 'Click to expand', icon: <ChevronDown size={18} style={{ color: '#22c55e' }} />, connected: true, isToggle: true, toggleAction: () => setSheetsExpanded(true) });
    }
  }
  if (currentCalendar) leftNodes.push({ id: 'calendar', label: 'Google Calendar', sub: 'Connected', icon: <CalendarIconSvg />, connected: true });
  if (currentFiles.length === 1) {
    leftNodes.push({ id: `file-${currentFiles[0]._id}`, label: currentFiles[0].originalName || currentFiles[0].name || 'File', sub: (currentFiles[0].purpose || 'knowledge_base').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), icon: <FileText size={18} style={{ color: '#22c55e' }} />, connected: true, action: () => openNodePanel('file', currentFiles[0]), actionLabel: 'Manage →' });
  } else if (currentFiles.length > 1) {
    leftNodes.push({ id: `file-${currentFiles[0]._id}`, label: currentFiles[0].originalName || currentFiles[0].name || 'File', sub: (currentFiles[0].purpose || 'knowledge_base').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), icon: <FileText size={18} style={{ color: '#22c55e' }} />, connected: true, action: () => openNodePanel('file', currentFiles[0]), actionLabel: 'Manage →' });
    if (filesExpanded) {
      currentFiles.slice(1).forEach((f, i) => {
        leftNodes.push({ id: `file-${f._id}`, label: f.originalName || f.name || 'File', sub: (f.purpose || 'knowledge_base').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), icon: <FileText size={18} style={{ color: '#22c55e' }} />, connected: true, isExpandedChild: true, _isFirstChild: i === 0, _collapseAction: () => setFilesExpanded(false), action: () => openNodePanel('file', f), actionLabel: 'Manage →' });
      });
    } else {
      leftNodes.push({ id: 'files-more', label: `+${currentFiles.length - 1} more file${currentFiles.length - 1 > 1 ? 's' : ''}`, sub: 'Click to expand', icon: <ChevronDown size={18} style={{ color: '#22c55e' }} />, connected: true, isToggle: true, toggleAction: () => setFilesExpanded(true) });
    }
  }
  // Always-visible "Add" placeholder nodes so merchant can add directly from canvas
  if (isGoogle) {
    leftNodes.push({ id: 'add-sheet', label: 'Connect Spreadsheet', sub: 'Click to add Google Sheet', icon: <Plus size={18} style={{ color: 'var(--primary)' }} />, connected: false, isAdd: true, action: () => { setSheetForm({ spreadsheetId: '', spreadsheetName: '', sheetName: '', purpose: 'price_list', purposeLabel: '', aiInstructions: '', columnMapping: {} }); setTabs([]); setPreview(null); setShowAddSheet(true); loadSpreadsheets(); } });
    leftNodes.push({ id: 'add-file', label: 'Upload Document', sub: 'PDF, DOCX, CSV, TXT', icon: <Upload size={18} style={{ color: 'var(--primary)' }} />, connected: false, isAdd: true, action: () => setShowUploadFile(true) });
  }

  // DB connection nodes
  dbConnections.forEach(db => {
    leftNodes.push({ id: `db-${db._id}`, label: db.name, sub: DB_TYPES.find(t => t.value === db.type)?.label || db.type, icon: <DbIcon type={db.type} />, connected: true, action: () => openNodePanel('db', db), actionLabel: 'Manage →' });
  });
  leftNodes.push({ id: 'add-db', label: 'Connect Database', sub: 'MySQL, PostgreSQL, MongoDB...', icon: <Database size={18} style={{ color: 'var(--primary)' }} />, connected: false, isAdd: true, action: () => setShowAddDB(true) });

  // Layout constants
  const canvasH = 500;
  const gap = 24;

  // Per-node rendered height — must match CSS padding + content
  const getNodeH = (n) => {
    if (n.isToggle) return 56;          // compact toggle node
    if (n.isAdd) return 88;             // add placeholder: smaller padding + action hint
    if (n.actionLabel) return 108;      // regular node + "Manage →" action bar
    return 76;                          // regular node, no action bar
  };

  // Position left nodes with cumulative Y so each uses its real height
  {
    let y = 0;
    const totalH = leftNodes.reduce((sum, n, i) => sum + getNodeH(n) + (i > 0 ? gap : 0), 0);
    const startY = Math.max(50, (canvasH - totalH) / 2);
    leftNodes.forEach(n => { n.x = 60; n.y = startY + y; y += getNodeH(n) + gap; });
  }
  const leftStartY = leftNodes.length > 0 ? leftNodes[0].y : canvasH / 2 - 38;
  const leftColTotal = leftNodes.reduce((sum, n, i) => sum + getNodeH(n) + (i > 0 ? gap : 0), 0);

  // Position right nodes centered
  const rightNodeH = 110; // channels have prompt badge
  const rightColTotal = wfChannels.length * rightNodeH + (wfChannels.length - 1) * gap;
  const rightStartY = Math.max(50, (canvasH - rightColTotal) / 2);

  // AI node — vertically centered between left and right
  const leftMidY = leftNodes.length > 0 ? leftStartY + leftColTotal / 2 - 38 : canvasH / 2 - 38;
  const rightMidY = wfChannels.length > 0 ? rightStartY + rightColTotal / 2 - 38 : canvasH / 2 - 38;
  const aiY = (leftMidY + rightMidY) / 2;

  // Canvas node definitions
  const canvasNodes = [
    ...leftNodes,
    { id: 'ai', label: activeWf ? activeWf.name : 'AI Assistant', sub: activeWf ? (activeWf.prompt?.name || 'Custom Prompt') : `${wfTotalSources} source${wfTotalSources !== 1 ? 's' : ''}`, icon: <Bot size={20} style={{ color: '#a78bfa' }} />, connected: true, x: 380, y: aiY, isCenter: true },
    // Channels scoped to workflow
    ...wfChannels.map((ch, i) => {
      const icons = {
        whatsapp: <svg width="18" height="18" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.5.5 0 00.612.638l4.685-1.228A11.934 11.934 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.259 0-4.35-.728-6.047-1.962l-.424-.312-2.78.729.744-2.716-.343-.545A9.936 9.936 0 012 12C2 6.486 6.486 2 12 2s10 4.486 10 10-4.486 10-10 10z"/></svg>,
        telegram: <svg width="18" height="18" viewBox="0 0 24 24" fill="#229ED9"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.248l-1.97 9.281c-.145.658-.537.818-1.084.508l-3-2.211-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.332-.373-.119l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.94z"/></svg>,
        facebook: <svg width="18" height="18" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
        instagram: <svg width="18" height="18" viewBox="0 0 24 24" fill="#E4405F"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0z"/></svg>,
        webchat: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>,
        kakaotalk: <svg width="18" height="18" viewBox="0 0 24 24" fill="#3C1E1E"><path d="M12 1C5.373 1 0 5.244 0 10.5c0 3.394 2.262 6.382 5.668 8.053-.247.93-.896 3.37-.929 3.573 0 0-.019.158.084.218.103.06.224.013.224.013.296-.041 3.434-2.267 3.973-2.652A14.32 14.32 0 0012 20c6.627 0 12-4.244 12-9.5S18.627 1 12 1z"/></svg>,
        tiktok: <svg width="18" height="18" viewBox="0 0 24 24" fill="#000000"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.52a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.62a8.2 8.2 0 004.76 1.52V6.69h-1z"/></svg>,
      };
      const labels = { whatsapp: 'WhatsApp', telegram: 'Telegram', facebook: 'Facebook', instagram: 'Instagram', webchat: 'Web Chat', kakaotalk: 'KakaoTalk', tiktok: 'TikTok' };
      const assignedPrompt = ch.customAiConfig?.promptId ? prompts.find(p => p._id === ch.customAiConfig.promptId) : null;
      return {
        id: `channel-${ch._id}`,
        label: labels[ch.type] || ch.type,
        sub: ch.name || 'Connected',
        promptName: assignedPrompt?.name || 'Global Prompt',
        icon: icons[ch.type] || <Users size={18} style={{ color: '#94a3b8' }} />,
        connected: true,
        x: 680,
        y: rightStartY + i * (rightNodeH + gap),
        isChannel: true,
      };
    }),
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Integrations</h1>
          <p className="page-subtitle">Connect data sources so your AI can answer customers accurately</p>
        </div>
        <div style={{ display: 'flex', gap: 4, background: 'var(--bg)', borderRadius: 8, padding: 3, border: '1px solid var(--border)' }}>
          <button onClick={() => setView('canvas')} style={{ padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.8rem', fontWeight: 600, background: view === 'canvas' ? 'var(--card-bg)' : 'transparent', color: view === 'canvas' ? 'var(--text)' : 'var(--text-muted)', boxShadow: view === 'canvas' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }}>
            <LayoutGrid size={14} /> Workflow
          </button>
          <button onClick={() => setView('list')} style={{ padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.8rem', fontWeight: 600, background: view === 'list' ? 'var(--card-bg)' : 'transparent', color: view === 'list' ? 'var(--text)' : 'var(--text-muted)', boxShadow: view === 'list' ? '0 1px 3px rgba(0,0,0,0.08)' : 'none' }}>
            <List size={14} /> List
          </button>
        </div>
      </div>

      {/* ============ WORKFLOW TABS ============ */}
      {view === 'canvas' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, padding: '0 4px', flexWrap: 'wrap' }}>
          <button onClick={() => { setSelectedWorkflow(null); setSheetsExpanded(false); setFilesExpanded(false); }}
            style={{
              padding: '7px 14px', borderRadius: 8, border: `2px solid ${!selectedWorkflow ? 'var(--primary)' : 'var(--border)'}`,
              background: !selectedWorkflow ? 'var(--primary-light)' : 'var(--card-bg)',
              color: !selectedWorkflow ? 'var(--primary)' : 'var(--text-muted)',
              fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
            }}>
            All Connections
          </button>
          {workflows.map(wf => (
            <div key={wf._id} style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
              <button onClick={() => { setSelectedWorkflow(wf._id); setSheetsExpanded(false); setFilesExpanded(false); }}
                style={{
                  padding: '7px 14px', borderRadius: '8px 0 0 8px', border: `2px solid ${selectedWorkflow === wf._id ? 'var(--primary)' : 'var(--border)'}`,
                  borderRight: 'none',
                  background: selectedWorkflow === wf._id ? 'var(--primary-light)' : 'var(--card-bg)',
                  color: selectedWorkflow === wf._id ? 'var(--primary)' : 'var(--text-muted)',
                  fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer',
                }}>
                {wf.name}
                {wf.channels?.length > 0 && <span style={{ marginLeft: 5, fontSize: '0.7rem', opacity: 0.7 }}>({wf.channels.length})</span>}
              </button>
              <button onClick={() => handleDeleteWorkflow(wf._id)} title="Delete workflow"
                style={{
                  padding: '7px 8px', borderRadius: '0 8px 8px 0', border: `2px solid ${selectedWorkflow === wf._id ? 'var(--primary)' : 'var(--border)'}`,
                  background: selectedWorkflow === wf._id ? 'var(--primary-light)' : 'var(--card-bg)',
                  color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.75rem',
                }}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
          {(() => {
            const maxWf = merchant?.limits?.maxWorkflows ?? 1;
            const atLimit = maxWf !== -1 && workflows.length >= maxWf;
            return atLimit ? (
              <button disabled style={{ padding: '7px 12px', borderRadius: 8, border: '2px dashed var(--border)', background: 'transparent', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.8rem', cursor: 'not-allowed', display: 'flex', alignItems: 'center', gap: 4, opacity: 0.5 }}>
                <Lock size={12} /> Limit ({maxWf}) — Upgrade
              </button>
            ) : (
              <button onClick={() => { setWorkflowForm({ name: '', description: '', prompt: { name: '', content: '' }, dataSources: { sheetIds: [], knowledgeFileIds: [], useCalendar: false } }); setShowCreateWorkflow(true); }}
                style={{ padding: '7px 12px', borderRadius: 8, border: '2px dashed var(--border)', background: 'transparent', color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Plus size={14} /> New Workflow
              </button>
            );
          })()}
        </div>
      )}

      {/* ============ CANVAS VIEW ============ */}
      {view === 'canvas' && (
        <div style={{ padding: '0 16px 16px' }}>
        <div
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{
            position: 'relative', width: '100%', height: 'calc(100vh - 220px)', minHeight: 460,
            background: ct.bg, borderRadius: 16, overflow: 'hidden',
            cursor: isPanning ? 'grabbing' : 'grab',
            transition: 'background 0.3s',
          }}
        >
          {/* Zoom & Theme Controls */}
          <div style={{ position: 'absolute', bottom: 20, right: 20, zIndex: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <button onClick={() => setCanvasTheme(p => p === 'dark' ? 'light' : 'dark')} title={isDark ? 'Light mode' : 'Dark mode'}
              style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${ct.border}`, background: ct.nodeBg, color: ct.text, fontSize: '0.9rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 4 }}>
              {isDark ? '☀️' : '🌙'}
            </button>
            <button onClick={zoomIn} style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${ct.border}`, background: ct.nodeBg, color: ct.text, fontSize: '1.1rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
            <button onClick={zoomOut} style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${ct.border}`, background: ct.nodeBg, color: ct.text, fontSize: '1.1rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>&minus;</button>
            <button onClick={resetView} style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${ct.border}`, background: ct.nodeBg, color: ct.textMuted, fontSize: '0.65rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>FIT</button>
            <div style={{ textAlign: 'center', fontSize: '0.66rem', color: ct.sectionLabel, fontWeight: 600, marginTop: 2 }}>{Math.round(zoom * 100)}%</div>
          </div>

          {/* Pannable + Zoomable inner layer */}
          <div style={{
            position: 'absolute', inset: 0,
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
            transition: isPanning ? 'none' : 'transform 0.15s ease',
            backgroundImage: `radial-gradient(circle, ${ct.dot} 1px, transparent 1px)`,
            backgroundSize: `${24}px ${24}px`,
            width: 960, height: Math.max(600, leftColTotal + 160, rightColTotal + 160),
          }}>

          {/* SVG Connection Lines */}
          <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
            <defs>
              <marker id="arrowG" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"><path d="M0,0 L8,3 L0,6" fill="#22c55e" /></marker>
              <marker id="arrowP" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"><path d="M0,0 L8,3 L0,6" fill="#a78bfa" /></marker>
              <marker id="arrowM" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto"><path d="M0,0 L8,3 L0,6" fill={ct.lineOff} /></marker>
            </defs>
            {/* Data sources → AI */}
            {canvasNodes.filter(n => !n.isCenter && !n.isChannel).map(src => {
              const ai = canvasNodes.find(n => n.id === 'ai');
              if (!src || !ai) return null;
              const srcMidY = src.y + getNodeH(src) / 2;
              const aiMidY = ai.y + 38;
              const color = src.connected ? '#22c55e' : ct.lineOff;
              const marker = src.connected ? 'url(#arrowG)' : 'url(#arrowM)';
              return (
                <path key={src.id}
                  d={`M${src.x + 210},${srcMidY} C${(src.x + 210 + ai.x) / 2},${srcMidY} ${(src.x + 210 + ai.x) / 2},${aiMidY} ${ai.x},${aiMidY}`}
                  fill="none" stroke={color} strokeWidth={src.connected ? 2.5 : 1.5}
                  strokeDasharray={src.connected ? 'none' : '6,4'}
                  markerEnd={marker}
                  style={{ opacity: src.connected ? 1 : 0.4 }}
                />
              );
            })}
            {/* AI → Connected Channels */}
            {canvasNodes.filter(n => n.isChannel).map(ch => {
              const ai = canvasNodes.find(n => n.id === 'ai');
              const aiMidY = ai.y + 38;
              const chMidY = ch.y + rightNodeH / 2;
              return (
                <path key={ch.id}
                  d={`M${ai.x + 210},${aiMidY} C${(ai.x + 210 + ch.x) / 2},${aiMidY} ${(ai.x + 210 + ch.x) / 2},${chMidY} ${ch.x},${chMidY}`}
                  fill="none" stroke="#a78bfa" strokeWidth={2}
                  markerEnd="url(#arrowP)"
                  style={{ opacity: 0.7 }}
                />
              );
            })}
          </svg>

          {/* Canvas Nodes */}
          {canvasNodes.map(node => (
            <div key={node.id} className="canvas-node" style={{
              position: 'absolute', left: node.x, top: node.y,
              width: 210, padding: node.isToggle || node.isAdd ? '10px 16px' : '14px 16px',
              background: node.isAdd ? (isDark ? '#0f1a2e' : '#f0f9ff') : node.isToggle ? (isDark ? '#1e1e3a' : '#f0fdf4') : node.isCenter ? ct.centerBg : ct.nodeBg,
              border: `2px solid ${node.isAdd ? 'var(--primary)' : node.isToggle ? (isDark ? '#2a4a3a' : '#86efac') : node.isCenter ? ct.borderPurple : node.connected ? ct.borderGreen : ct.border}`,
              borderRadius: 14,
              boxShadow: node.isCenter ? ct.shadowCenter : node.connected ? ct.shadowGreen : ct.shadowNode,
              cursor: node.isToggle || node.action || node.isAdd ? 'pointer' : 'default',
              transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
              transform: node.isExpandedChild ? 'scale(1)' : undefined,
              animation: node.isExpandedChild ? 'nodeSlideIn 0.35s cubic-bezier(0.4, 0, 0.2, 1)' : undefined,
              zIndex: node.isToggle ? 3 : 2,
              borderStyle: node.isToggle || node.isAdd ? 'dashed' : 'solid',
              opacity: node.isAdd ? 0.85 : 1,
            }}
              onClick={node.isToggle ? node.toggleAction : node.action || undefined}
            >
              {/* Collapse button for expanded children — show on first expanded child */}
              {node.isExpandedChild && node._isFirstChild && (
                <div onClick={(e) => { e.stopPropagation(); node._collapseAction(); }}
                  style={{ position: 'absolute', top: -10, right: 8, width: 20, height: 20, borderRadius: '50%', background: ct.borderGreen, border: `2px solid ${ct.dotBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 5 }}>
                  <ChevronUp size={12} style={{ color: '#fff' }} />
                </div>
              )}
              {/* Connection dots */}
              {!node.isCenter && !node.isChannel && !node.isToggle && (
                <div style={{ position: 'absolute', right: -7, top: '50%', transform: 'translateY(-50%)', width: 12, height: 12, borderRadius: '50%', background: node.connected ? ct.borderGreen : ct.border, border: `2px solid ${ct.dotBorder}` }} />
              )}
              {node.isToggle && (
                <div style={{ position: 'absolute', right: -7, top: '50%', transform: 'translateY(-50%)', width: 12, height: 12, borderRadius: '50%', background: ct.borderGreen, border: `2px solid ${ct.dotBorder}`, opacity: 0.5 }} />
              )}
              {node.isCenter && <>
                <div style={{ position: 'absolute', left: -7, top: '50%', transform: 'translateY(-50%)', width: 12, height: 12, borderRadius: '50%', background: ct.borderPurple, border: `2px solid ${ct.dotBorder}` }} />
                <div style={{ position: 'absolute', right: -7, top: '50%', transform: 'translateY(-50%)', width: 12, height: 12, borderRadius: '50%', background: ct.borderPurple, border: `2px solid ${ct.dotBorder}` }} />
              </>}
              {node.isChannel && (
                <div style={{ position: 'absolute', left: -7, top: '50%', transform: 'translateY(-50%)', width: 12, height: 12, borderRadius: '50%', background: '#a78bfa', border: `2px solid ${ct.dotBorder}` }} />
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                  background: node.isAdd ? (isDark ? '#0a1628' : '#e0f2fe') : node.isToggle ? (isDark ? '#1a3a2a' : '#dcfce7') : node.isCenter ? ct.centerIconBox : ct.iconBox,
                  border: `1.5px solid ${node.isAdd ? 'var(--primary)' : node.isCenter ? ct.borderPurple : node.connected ? ct.borderGreen + '40' : ct.iconBoxBorder}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {node.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: node.isToggle || node.isAdd ? 600 : 700, fontSize: node.isToggle || node.isAdd ? '0.78rem' : '0.82rem', color: node.isAdd ? 'var(--primary)' : node.isToggle ? ct.borderGreen : ct.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{node.label}</div>
                  <div style={{
                    fontSize: '0.7rem', marginTop: 1,
                    color: node.isAdd ? (isDark ? '#64748b' : '#94a3b8') : node.isToggle ? ct.textMuted : (node.connected || node.isCenter ? ct.borderGreen : ct.textDim),
                  }}>
                    {node.connected && !node.isCenter && !node.isToggle && !node.isAdd && <CheckCircle size={9} style={{ verticalAlign: -1, marginRight: 3 }} />}
                    {node.sub}
                  </div>
                </div>
              </div>

              {/* Prompt badge for channels */}
              {node.isChannel && node.promptName && (
                <div style={{
                  marginTop: 6, padding: '3px 8px', borderRadius: 6,
                  background: isDark ? '#1e1b4b' : '#ede9fe',
                  border: `1px solid ${isDark ? '#3b2f80' : '#c4b5fd'}`,
                  fontSize: '0.66rem', fontWeight: 600,
                  color: isDark ? '#a78bfa' : '#7c3aed',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  <Bot size={9} style={{ verticalAlign: -1, marginRight: 3 }} />
                  {node.promptName}
                </div>
              )}

              {/* Action hint */}
              {node.action && !node.isCenter && !node.isToggle && (
                <div style={{
                  marginTop: 8, padding: '4px 0', textAlign: 'center',
                  borderTop: `1px solid ${ct.border}`, fontSize: '0.7rem', fontWeight: 600,
                  color: ct.textMuted,
                }}>
                  {node.actionLabel || 'Click to manage'}
                </div>
              )}
            </div>
          ))}

          {/* Labels */}
          {leftNodes.length > 0 && <div style={{ position: 'absolute', left: 100, top: leftStartY - 24, fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, color: ct.sectionLabel }}>Data Sources</div>}
          <div style={{ position: 'absolute', left: 420, top: aiY - 24, fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, color: ct.sectionLabel }}>{activeWf ? 'Workflow' : 'AI Engine'}</div>
          {wfChannels.length > 0 && <div style={{ position: 'absolute', left: 720, top: rightStartY - 24, fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, color: ct.sectionLabel }}>Channels ({wfChannels.length})</div>}

          </div>{/* end pannable layer */}

          {/* Empty state — not connected to Google at all */}
          {!isGoogle && (
            <div style={{ position: 'absolute', bottom: 20, left: 20, zIndex: 10, padding: '8px 14px', borderRadius: 10, background: ct.badgeBg, border: `1px solid ${ct.badgeBorder}` }}>
              <button className="btn btn-primary btn-sm" onClick={connectGoogle}><GoogleIcon /> &nbsp;Connect Google to get started</button>
            </div>
          )}

          {/* Node Action Slide Panel — appears from right when a node is clicked */}
          {selectedNode && (
            <div style={{ position: 'absolute', inset: 0, zIndex: 30 }} onClick={() => setSelectedNode(null)}>
              <div style={{
                position: 'absolute', right: 0, top: 0, bottom: 0, width: 300,
                background: isDark ? '#1a1a2e' : '#fff',
                borderLeft: `1px solid ${isDark ? '#2a2a4a' : '#e2e8f0'}`,
                boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
                display: 'flex', flexDirection: 'column',
                animation: 'slideInRight 0.2s ease',
              }} onClick={e => e.stopPropagation()}>

                {/* Panel Header */}
                <div style={{ padding: '16px 18px', borderBottom: `1px solid ${isDark ? '#2a2a4a' : '#f1f5f9'}`, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem', color: ct.text }}>
                      {selectedNode.type === 'sheet' ? 'Spreadsheet' : selectedNode.type === 'file' ? 'Knowledge File' : selectedNode.type === 'db' ? 'Database' : selectedNode.type === 'add_sheet' ? 'Connect Spreadsheet' : 'Upload Document'}
                    </div>
                    {selectedNode.data && <div style={{ fontSize: '0.72rem', color: ct.textMuted, marginTop: 2 }}>{selectedNode.data.spreadsheetName || selectedNode.data.originalName || ''}</div>}
                  </div>
                  <button onClick={() => setSelectedNode(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: ct.textMuted, padding: 4 }}><X size={16} /></button>
                </div>

                {/* Sheet panel */}
                {selectedNode.type === 'sheet' && selectedNode.data && (
                  <div style={{ padding: '16px 18px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ padding: '10px 12px', background: isDark ? '#16162a' : '#f8fafc', borderRadius: 10, border: `1px solid ${isDark ? '#2a2a4a' : '#e2e8f0'}` }}>
                      <div style={{ fontWeight: 600, fontSize: '0.82rem', color: ct.text }}>{selectedNode.data.sheetName}</div>
                      <div style={{ fontSize: '0.72rem', color: ct.textMuted, marginTop: 3 }}>{selectedNode.data.spreadsheetName}</div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                        <span style={{ background: '#dbeafe', color: '#2563eb', padding: '2px 8px', borderRadius: 4, fontSize: '0.66rem', fontWeight: 600 }}>{(selectedNode.data.purpose || '').replace(/_/g, ' ')}</span>
                        {selectedNode.data.rowCount > 0 && <span style={{ fontSize: '0.7rem', color: ct.textMuted }}>{selectedNode.data.rowCount} rows</span>}
                        {selectedNode.data.lastSynced && <span style={{ fontSize: '0.7rem', color: ct.textMuted }}>Synced {new Date(selectedNode.data.lastSynced).toLocaleDateString()}</span>}
                      </div>
                    </div>

                    {/* AI Instructions */}
                    <div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: ct.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>AI Instructions</div>
                      {!nodePanelEditingInstr ? (
                        <div style={{ padding: '8px 10px', background: isDark ? '#16162a' : '#f8fafc', borderRadius: 8, border: `1px solid ${isDark ? '#2a2a4a' : '#e2e8f0'}`, fontSize: '0.78rem', color: nodePanelInstructions ? ct.text : ct.textMuted, cursor: 'pointer', minHeight: 60 }}
                          onClick={() => setNodePanelEditingInstr(true)}>
                          {nodePanelInstructions || 'Click to add instructions for AI…'}
                        </div>
                      ) : (
                        <div>
                          <textarea className="form-input" rows={4} value={nodePanelInstructions} onChange={e => setNodePanelInstructions(e.target.value)} placeholder="e.g. Use this sheet when customers ask about pricing" style={{ fontSize: '0.8rem', width: '100%' }} autoFocus />
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 6 }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => { setNodePanelEditingInstr(false); setNodePanelInstructions(selectedNode.data.aiInstructions || ''); }}>Cancel</button>
                            <button className="btn btn-primary btn-sm" onClick={() => savePanelSheetInstructions(selectedNode.data._id, nodePanelInstructions)}>Save</button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-secondary" style={{ flex: 1, fontSize: '0.78rem' }} disabled={syncing === selectedNode.data._id} onClick={() => syncSheet(selectedNode.data._id)}>
                        <RefreshCw size={13} style={syncing === selectedNode.data._id ? { animation: 'spin 0.7s linear infinite' } : {}} /> Sync Data
                      </button>
                      <button className="btn btn-secondary" style={{ fontSize: '0.78rem', color: '#ef4444', borderColor: '#ef4444' }} onClick={() => { disconnectSheet(selectedNode.data._id); setSelectedNode(null); }}>
                        <Trash2 size={13} /> Remove
                      </button>
                    </div>
                  </div>
                )}

                {/* File panel */}
                {selectedNode.type === 'file' && selectedNode.data && (
                  <div style={{ padding: '16px 18px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ padding: '10px 12px', background: isDark ? '#16162a' : '#f8fafc', borderRadius: 10, border: `1px solid ${isDark ? '#2a2a4a' : '#e2e8f0'}` }}>
                      <div style={{ fontWeight: 600, fontSize: '0.82rem', color: ct.text, wordBreak: 'break-word' }}>{selectedNode.data.originalName || selectedNode.data.name}</div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ background: '#dbeafe', color: '#2563eb', padding: '2px 8px', borderRadius: 4, fontSize: '0.66rem', fontWeight: 600 }}>{(selectedNode.data.purpose || 'knowledge_base').replace(/_/g, ' ')}</span>
                        <span style={{ fontSize: '0.7rem', color: ct.textMuted }}>{formatFileSize(selectedNode.data.fileSize)}</span>
                        <span style={{ fontSize: '0.7rem', color: selectedNode.data.status === 'ready' ? '#22c55e' : '#f59e0b', fontWeight: 600 }}>{selectedNode.data.status}</span>
                      </div>
                      {selectedNode.data.createdAt && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 8, fontSize: '0.7rem', color: ct.textMuted }}>
                          <Clock size={11} /> Uploaded {formatUploadedAt(selectedNode.data.createdAt)}
                        </div>
                      )}
                    </div>

                    {/* AI Instructions */}
                    <div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: ct.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>AI Instructions</div>
                      {!nodePanelEditingInstr ? (
                        <div style={{ padding: '8px 10px', background: isDark ? '#16162a' : '#f8fafc', borderRadius: 8, border: `1px solid ${isDark ? '#2a2a4a' : '#e2e8f0'}`, fontSize: '0.78rem', color: nodePanelInstructions ? ct.text : ct.textMuted, cursor: 'pointer', minHeight: 60 }}
                          onClick={() => setNodePanelEditingInstr(true)}>
                          {nodePanelInstructions || 'Click to add instructions for AI…'}
                        </div>
                      ) : (
                        <div>
                          <textarea className="form-input" rows={4} value={nodePanelInstructions} onChange={e => setNodePanelInstructions(e.target.value)} placeholder="e.g. Use this file when customers ask about careers" style={{ fontSize: '0.8rem', width: '100%' }} autoFocus />
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 6 }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => { setNodePanelEditingInstr(false); setNodePanelInstructions(selectedNode.data.aiInstructions || ''); }}>Cancel</button>
                            <button className="btn btn-primary btn-sm" onClick={() => savePanelFileInstructions(selectedNode.data._id, nodePanelInstructions)}>Save</button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>
                      <button className="btn btn-secondary" style={{ flex: 1, fontSize: '0.78rem', justifyContent: 'center' }} onClick={() => downloadFile(selectedNode.data)}>
                        <Download size={13} /> Download
                      </button>
                      <button className="btn btn-secondary" style={{ flex: 1, fontSize: '0.78rem', color: '#ef4444', borderColor: '#ef4444', justifyContent: 'center' }} onClick={() => { deleteFile(selectedNode.data._id); setSelectedNode(null); }}>
                        <Trash2 size={13} /> Delete
                      </button>
                    </div>
                  </div>
                )}

                {/* DB panel */}
                {selectedNode.type === 'db' && selectedNode.data && (
                  <div style={{ padding: '16px 18px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ padding: '10px 12px', background: isDark ? '#16162a' : '#f8fafc', borderRadius: 10, border: `1px solid ${isDark ? '#2a2a4a' : '#e2e8f0'}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <DbIcon type={selectedNode.data.type} size={20} />
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '0.82rem', color: ct.text }}>{selectedNode.data.name}</div>
                          <div style={{ fontSize: '0.72rem', color: ct.textMuted, marginTop: 2 }}>
                            {DB_TYPES.find(t => t.value === selectedNode.data.type)?.label}
                            {selectedNode.data.host ? ` · ${selectedNode.data.host}:${selectedNode.data.port}` : selectedNode.data.database ? ` · ${selectedNode.data.database}` : ''}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: ct.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 1 }}>AI Instructions</div>
                      {!nodePanelEditingInstr ? (
                        <div style={{ padding: '8px 10px', background: isDark ? '#16162a' : '#f8fafc', borderRadius: 8, border: `1px solid ${isDark ? '#2a2a4a' : '#e2e8f0'}`, fontSize: '0.78rem', color: nodePanelInstructions ? ct.text : ct.textMuted, cursor: 'pointer', minHeight: 60 }}
                          onClick={() => setNodePanelEditingInstr(true)}>
                          {nodePanelInstructions || 'Click to add instructions for AI…'}
                        </div>
                      ) : (
                        <div>
                          <textarea className="form-input" rows={4} value={nodePanelInstructions} onChange={e => setNodePanelInstructions(e.target.value)} placeholder="e.g. Use this database when customers ask about products" style={{ fontSize: '0.8rem', width: '100%' }} autoFocus />
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 6 }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => { setNodePanelEditingInstr(false); setNodePanelInstructions(selectedNode.data.aiInstructions || ''); }}>Cancel</button>
                            <button className="btn btn-primary btn-sm" onClick={() => handleSaveDBInstructions(selectedNode.data._id, nodePanelInstructions)}>Save</button>
                          </div>
                        </div>
                      )}
                    </div>

                    <button className="btn btn-secondary" style={{ fontSize: '0.78rem', color: '#ef4444', borderColor: '#ef4444', justifyContent: 'center' }} onClick={() => { handleDisconnectDB(selectedNode.data._id); setSelectedNode(null); }}>
                      <Trash2 size={13} /> Disconnect
                    </button>
                  </div>
                )}

                {/* Add Sheet panel — opens the add sheet form inline */}
                {selectedNode.type === 'add_sheet' && (
                  <div style={{ padding: '16px 18px', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, textAlign: 'center' }}>
                    <SheetsIcon />
                    <div style={{ fontWeight: 600, color: ct.text }}>Connect a Google Sheet</div>
                    <div style={{ fontSize: '0.78rem', color: ct.textMuted }}>Use data from your spreadsheets to answer customer questions about prices, services, FAQs, and more.</div>
                    <button className="btn btn-primary" onClick={() => { setSheetForm({ spreadsheetId: '', spreadsheetName: '', sheetName: '', purpose: 'price_list', purposeLabel: '', aiInstructions: '', columnMapping: {} }); setTabs([]); setPreview(null); setShowAddSheet(true); loadSpreadsheets(); setSelectedNode(null); }}>
                      <Plus size={14} /> Pick Spreadsheet
                    </button>
                  </div>
                )}

                {/* Add File panel */}
                {selectedNode.type === 'add_file' && (
                  <div style={{ padding: '16px 18px', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, textAlign: 'center' }}>
                    <FileText size={32} style={{ color: 'var(--primary)' }} />
                    <div style={{ fontWeight: 600, color: ct.text }}>Upload a Document</div>
                    <div style={{ fontSize: '0.78rem', color: ct.textMuted }}>Upload PDFs, Word docs, spreadsheets, or text files as knowledge base documents for your AI.</div>
                    <button className="btn btn-primary" onClick={() => { setShowUploadFile(true); setSelectedNode(null); }}><Upload size={14} /> Upload File</button>
                    {isGoogle && <button className="btn btn-secondary" onClick={() => { openPicker(); setSelectedNode(null); }}><DriveIcon /> Import from Drive</button>}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        </div>
      )}

      {/* ============ LIST VIEW ============ */}
      {view === 'list' && <div className="page-body" style={{ maxWidth: 680, margin: '0 auto' }}>

        {/* ====== FLOW: Google Account Node ====== */}
        <div style={nodeStyle(isGoogle)}>
          <div style={{ ...dotStyle('right', isGoogle) }} />
          <div style={nodeHeaderStyle(false)}>
            <div style={nodeIconBox(isGoogle)}><GoogleIcon /></div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>Google Account</div>
              <div style={{ fontSize: '0.76rem', color: isGoogle ? '#22c55e' : '#94a3b8', marginTop: 1 }}>
                {isGoogle ? <><CheckCircle size={11} style={{ verticalAlign: -1, marginRight: 4 }} />{googleStatus.googleEmail}</> : 'Connect to unlock Sheets, Calendar & Drive'}
              </div>
            </div>
            {isGoogle ? (
              <button className="btn btn-secondary btn-sm" onClick={disconnectGoogle}>Disconnect</button>
            ) : (
              <button className="btn btn-primary btn-sm" onClick={connectGoogle}><ExternalLink size={13} /> Connect</button>
            )}
          </div>
          {/* Warn if connected with old read-only scope — needs reconnect to save data to sheets */}
          {isGoogle && (() => {
            const scope = googleStatus?.scope || '';
            // Full write scope: auth/spreadsheets (not followed by .readonly)
            const hasWriteScope = /auth\/spreadsheets(?!\.)/.test(scope);
            if (!hasWriteScope) return (
              <div style={{ margin: '0 16px 14px', padding: '10px 14px', background: '#fff7ed', border: '1px solid #fb923c', borderRadius: 10, fontSize: '0.8rem', color: '#c2410c', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontWeight: 700 }}>⚠ Reconnect required</span>
                <span style={{ flex: 1 }}>Your Google connection needs updated permissions to save appointments, leads, and tickets to spreadsheets.</span>
                <button className="btn btn-sm" style={{ background: '#fb923c', color: '#fff', border: 'none', whiteSpace: 'nowrap' }} onClick={connectGoogle}>Reconnect Now</button>
              </div>
            );
            return (
              <div style={{ margin: '0 16px 14px', padding: '8px 14px', background: '#f0fdf4', border: '1px solid #22c55e', borderRadius: 10, fontSize: '0.78rem', color: '#15803d', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>✓ Sheet write permissions active — data will be saved automatically</span>
              </div>
            );
          })()}
        </div>

        {/* Connector line */}
        <div style={connectorLine} />

        {/* ====== FLOW: Data Sources Grid ====== */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* --- Google Sheets Node --- */}
          <div style={nodeStyle(connectedSheets.length > 0)}>
            <div style={{ ...dotStyle('left', connectedSheets.length > 0) }} />
            <div style={{ ...dotStyle('right', connectedSheets.length > 0) }} />
            <div style={nodeHeaderStyle(true)} onClick={() => tog('sheets')}>
              <div style={nodeIconBox(connectedSheets.length > 0)}><SheetsIcon /></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>
                  Google Sheets
                  {connectedSheets.length > 0 && <span style={{ fontWeight: 500, fontSize: '0.78rem', color: '#22c55e', marginLeft: 8 }}>{connectedSheets.length} connected</span>}
                </div>
                <div style={{ fontSize: '0.76rem', color: '#94a3b8', marginTop: 1 }}>Prices, products, menus, FAQs from spreadsheets</div>
              </div>
              {isGoogle && (
                <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); setSheetForm({ spreadsheetId: '', spreadsheetName: '', sheetName: '', purpose: 'price_list', purposeLabel: '', aiInstructions: '', columnMapping: {} }); setTabs([]); setPreview(null); setShowAddSheet(true); loadSpreadsheets(); }}>
                  <Plus size={12} /> Add
                </button>
              )}
              {connectedSheets.length > 0 && (open.sheets ? <ChevronUp size={16} style={{ color: '#94a3b8' }} /> : <ChevronDown size={16} style={{ color: '#94a3b8' }} />)}
            </div>

            {/* Expanded: scrollable list */}
            {open.sheets && connectedSheets.length > 0 && (
              <div style={{ borderTop: '1px solid #f1f5f9', padding: '0 16px 14px', maxHeight: 280, overflowY: 'auto' }}>
                {connectedSheets.map(sheet => (
                  <div key={sheet._id} style={{ padding: '10px 12px', margin: '10px 0 0', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <SheetsIcon />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sheet.spreadsheetName} &rarr; {sheet.sheetName}</div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                          <span style={{ background: '#dbeafe', color: '#2563eb', padding: '1px 6px', borderRadius: 4, fontSize: '0.66rem', fontWeight: 600 }}>{(sheet.purpose || '').replace('_', ' ')}</span>
                          {sheet.rowCount > 0 && <span>{sheet.rowCount} rows</span>}
                          {sheet.lastSynced && <span>{new Date(sheet.lastSynced).toLocaleDateString()}</span>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 2 }}>
                        <button className="btn btn-ghost btn-sm" title="AI Instructions" onClick={() => { setEditingInstructions(sheet._id); setInstructionsText(sheet.aiInstructions || ''); }}><Settings size={12} /></button>
                        <button className="btn btn-ghost btn-sm" title="Sync" disabled={syncing === sheet._id} onClick={() => syncSheet(sheet._id)}><RefreshCw size={12} style={syncing === sheet._id ? { animation: 'spin 0.7s linear infinite' } : {}} /></button>
                        <button className="btn btn-ghost btn-sm" title="Remove" onClick={() => disconnectSheet(sheet._id)}><Trash2 size={12} /></button>
                      </div>
                    </div>
                    {sheet.aiInstructions && editingInstructions !== sheet._id && (
                      <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 6, padding: '5px 8px', background: '#fff', borderRadius: 6, border: '1px solid #f1f5f9' }}><strong>AI:</strong> {sheet.aiInstructions}</div>
                    )}
                    {editingInstructions === sheet._id && (
                      <div style={{ marginTop: 8 }}>
                        <textarea className="form-input" rows={2} value={instructionsText} onChange={e => setInstructionsText(e.target.value)} placeholder="Tell AI when to use this sheet..." style={{ fontSize: '0.8rem' }} />
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 6 }}><button className="btn btn-secondary btn-sm" onClick={() => setEditingInstructions(null)}>Cancel</button><button className="btn btn-primary btn-sm" onClick={() => saveInstructions(sheet._id)}>Save</button></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {open.sheets && connectedSheets.length === 0 && (
              <div style={{ borderTop: '1px solid #f1f5f9', padding: '16px', color: '#94a3b8', fontSize: '0.82rem', textAlign: 'center' }}>
                {isGoogle ? 'No sheets yet. Click "Add" to connect your first spreadsheet.' : 'Connect Google Account first.'}
              </div>
            )}
          </div>

          {/* --- Google Calendar Node --- */}
          <div style={nodeStyle(calendarStatus?.isActive)}>
            <div style={{ ...dotStyle('left', calendarStatus?.isActive) }} />
            <div style={{ ...dotStyle('right', calendarStatus?.isActive) }} />
            <div style={nodeHeaderStyle(false)}>
              <div style={nodeIconBox(calendarStatus?.isActive)}><CalendarIconSvg /></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>Google Calendar</div>
                <div style={{ fontSize: '0.76rem', color: calendarStatus?.isActive ? '#22c55e' : '#94a3b8', marginTop: 1 }}>
                  {calendarStatus?.isActive ? <><CheckCircle size={11} style={{ verticalAlign: -1, marginRight: 4 }} />Connected &mdash; AI checks your availability</> : 'Let AI check availability for bookings'}
                </div>
              </div>
              {isGoogle && (
                calendarStatus?.isActive ? (
                  <button className="btn btn-secondary btn-sm" onClick={handleDisconnectCalendar}>Disconnect</button>
                ) : (
                  <button className="btn btn-primary btn-sm" onClick={handleConnectCalendar} disabled={connectingCalendar}>{connectingCalendar ? '...' : 'Connect'}</button>
                )
              )}
            </div>
          </div>

          {/* --- Google Drive Node --- */}
          <div style={nodeStyle(false)}>
            <div style={{ ...dotStyle('left', false) }} />
            <div style={{ ...dotStyle('right', false) }} />
            <div style={nodeHeaderStyle(false)}>
              <div style={nodeIconBox(false)}><DriveIcon /></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>Google Drive</div>
                <div style={{ fontSize: '0.76rem', color: '#94a3b8', marginTop: 1 }}>Import files directly from your Drive</div>
              </div>
              {isGoogle && (
                <button className="btn btn-primary btn-sm" onClick={openPicker} disabled={pickerLoading}>{pickerLoading ? '...' : <><Plus size={12} /> Pick Files</>}</button>
              )}
            </div>
          </div>

          {/* --- Knowledge Base Files Node --- */}
          <div style={nodeStyle(files.length > 0)}>
            <div style={{ ...dotStyle('left', files.length > 0) }} />
            <div style={{ ...dotStyle('right', files.length > 0) }} />
            <div style={nodeHeaderStyle(true)} onClick={() => tog('files')}>
              <div style={nodeIconBox(files.length > 0)}><FileText size={18} style={{ color: files.length > 0 ? '#22c55e' : '#94a3b8' }} /></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>
                  Knowledge Base
                  {files.length > 0 && <span style={{ fontWeight: 500, fontSize: '0.78rem', color: '#22c55e', marginLeft: 8 }}>{files.length} file{files.length > 1 ? 's' : ''}</span>}
                </div>
                <div style={{ fontSize: '0.76rem', color: '#94a3b8', marginTop: 1 }}>Upload PDF, Word, Excel, CSV, TXT</div>
              </div>
              <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); setShowUploadFile(true); }}><Upload size={12} /> Upload</button>
              {files.length > 0 && (open.files ? <ChevronUp size={16} style={{ color: '#94a3b8' }} /> : <ChevronDown size={16} style={{ color: '#94a3b8' }} />)}
            </div>

            {open.files && files.length > 0 && (
              <div style={{ borderTop: '1px solid #f1f5f9', padding: '0 16px 14px', maxHeight: 280, overflowY: 'auto' }}>
                {files.map(file => (
                  <div key={file._id} style={{ padding: '10px 12px', margin: '10px 0 0', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <File size={15} style={{ color: '#94a3b8', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.originalName || file.name}</div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                          <span style={{ background: '#dbeafe', color: '#2563eb', padding: '1px 6px', borderRadius: 4, fontSize: '0.66rem', fontWeight: 600 }}>{getFileTypeBadge(file.originalName || file.name)}</span>
                          <span>{formatFileSize(file.fileSize || file.size)}</span>
                          <span style={{
                            padding: '1px 6px', borderRadius: 4, fontSize: '0.66rem', fontWeight: 600,
                            background: file.status === 'ready' ? '#dcfce7' : file.status === 'error' ? '#fee2e2' : '#fef3c7',
                            color: file.status === 'ready' ? '#16a34a' : file.status === 'error' ? '#dc2626' : '#d97706',
                          }}>{file.status || 'processing'}</span>
                          <span style={{ background: '#f1f5f9', color: '#64748b', padding: '1px 6px', borderRadius: 4, fontSize: '0.66rem', fontWeight: 600 }}>{(file.purpose || 'custom').replace('_', ' ')}</span>
                          {file.createdAt && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#94a3b8' }}>
                              <Clock size={10} /> {formatUploadedAt(file.createdAt)}
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => downloadFile(file)} title="Download"><Download size={12} /></button>
                        <button className="btn btn-ghost btn-sm" onClick={() => { setEditingFileInstructions(file._id); setFileInstructionsText(file.aiInstructions || ''); }} title="AI instructions"><Settings size={12} /></button>
                        <button className="btn btn-ghost btn-sm" onClick={() => deleteFile(file._id)} title="Delete"><Trash2 size={12} /></button>
                      </div>
                    </div>
                    {file.aiInstructions && editingFileInstructions !== file._id && (
                      <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 6, padding: '5px 8px', background: '#fff', borderRadius: 6, border: '1px solid #f1f5f9' }}><strong>AI:</strong> {file.aiInstructions}</div>
                    )}
                    {editingFileInstructions === file._id && (
                      <div style={{ marginTop: 8 }}>
                        <textarea className="form-input" rows={2} value={fileInstructionsText} onChange={e => setFileInstructionsText(e.target.value)} placeholder="Tell AI when to use this file..." style={{ fontSize: '0.8rem' }} />
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 6 }}><button className="btn btn-secondary btn-sm" onClick={() => setEditingFileInstructions(null)}>Cancel</button><button className="btn btn-primary btn-sm" onClick={() => saveFileInstructions(file._id)}>Save</button></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {open.files && files.length === 0 && (
              <div style={{ borderTop: '1px solid #f1f5f9', padding: '16px', color: '#94a3b8', fontSize: '0.82rem', textAlign: 'center' }}>No files yet. Upload documents for your AI to learn from.</div>
            )}
          </div>

          {/* --- Database Connections Node --- */}
          <div style={nodeStyle(dbConnections.length > 0)}>
            <div style={{ ...dotStyle('left', dbConnections.length > 0) }} />
            <div style={{ ...dotStyle('right', dbConnections.length > 0) }} />
            <div style={nodeHeaderStyle(true)} onClick={() => tog('databases')}>
              <div style={nodeIconBox(dbConnections.length > 0)}>
                <Database size={18} style={{ color: dbConnections.length > 0 ? '#22c55e' : '#94a3b8' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>
                  Databases
                  {dbConnections.length > 0 && <span style={{ fontWeight: 500, fontSize: '0.78rem', color: '#22c55e', marginLeft: 8 }}>{dbConnections.length} connected</span>}
                </div>
                <div style={{ fontSize: '0.76rem', color: '#94a3b8', marginTop: 1 }}>MySQL, PostgreSQL, SQL Server, MongoDB, SQLite</div>
              </div>
              <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); setShowAddDB(true); }}>
                <Plus size={12} /> Connect
              </button>
              {dbConnections.length > 0 && (open.databases ? <ChevronUp size={16} style={{ color: '#94a3b8' }} /> : <ChevronDown size={16} style={{ color: '#94a3b8' }} />)}
            </div>

            {open.databases && dbConnections.length > 0 && (
              <div style={{ borderTop: '1px solid #f1f5f9', padding: '0 16px 14px', maxHeight: 280, overflowY: 'auto' }}>
                {dbConnections.map(db => {
                  const dbType = DB_TYPES.find(t => t.value === db.type);
                  return (
                    <div key={db._id} style={{ padding: '10px 12px', margin: '10px 0 0', background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <DbIcon type={db.type} size={16} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: '0.82rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{db.name}</div>
                          <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                            <span style={{ background: dbType ? `${dbType.color}18` : '#f1f5f9', color: dbType?.color || '#64748b', padding: '1px 6px', borderRadius: 4, fontSize: '0.66rem', fontWeight: 600 }}>{dbType?.label || db.type}</span>
                            {db.host && <span>{db.host}:{db.port}</span>}
                            <span style={{ background: '#dcfce7', color: '#16a34a', padding: '1px 6px', borderRadius: 4, fontSize: '0.66rem', fontWeight: 600 }}>connected</span>
                          </div>
                        </div>
                        <button className="btn btn-ghost btn-sm" title="View tables / collections" onClick={() => setShowTablesDb(db)} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: '0.7rem' }}>
                          <Table2 size={12} /> Tables
                        </button>
                        <button className="btn btn-ghost btn-sm" title="Edit connection" onClick={() => setEditingDb(db)}>
                          <Pencil size={12} />
                        </button>
                        <button className="btn btn-ghost btn-sm" title="Disconnect" onClick={() => handleDisconnectDB(db._id)}><Trash2 size={12} /></button>
                      </div>
                      {db.aiInstructions && (
                        <div style={{ fontSize: '0.7rem', color: '#64748b', marginTop: 6, padding: '5px 8px', background: '#fff', borderRadius: 6, border: '1px solid #f1f5f9' }}><strong>AI:</strong> {db.aiInstructions}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {open.databases && dbConnections.length === 0 && (
              <div style={{ borderTop: '1px solid #f1f5f9', padding: '16px', color: '#94a3b8', fontSize: '0.82rem', textAlign: 'center' }}>
                No databases connected. Click "Connect" to add your first database.
              </div>
            )}
          </div>
        </div>

        {/* Connector line */}
        <div style={connectorLine} />

        {/* ====== FLOW: AI Brain Node ====== */}
        <div style={{ ...nodeStyle(true), borderColor: 'var(--primary, #6366f1)', boxShadow: '0 0 0 3px rgba(99,102,241,0.1)' }}>
          <div style={{ ...dotStyle('left', true), background: 'var(--primary, #6366f1)' }} />
          <div style={{ ...dotStyle('right', true), background: 'var(--primary, #6366f1)' }} />
          <div style={nodeHeaderStyle(false)}>
            <div style={{ ...nodeIconBox(true), background: 'var(--primary-light, #eef2ff)', borderColor: 'var(--primary, #6366f1)' }}>
              <Bot size={20} style={{ color: 'var(--primary, #6366f1)' }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>AI Assistant</div>
              <div style={{ fontSize: '0.76rem', color: '#94a3b8', marginTop: 1 }}>
                Reads all connected data &bull; Answers customers accurately
              </div>
            </div>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--primary, #6366f1)', background: 'var(--primary-light, #eef2ff)', padding: '4px 10px', borderRadius: 6 }}>
              {connectedSheets.length + files.length + (calendarStatus?.isActive ? 1 : 0)} source{(connectedSheets.length + files.length + (calendarStatus?.isActive ? 1 : 0)) !== 1 ? 's' : ''}
            </div>
          </div>
        </div>

        <div style={connectorLine} />

        {/* ====== FLOW: Customers Node ====== */}
        <div style={nodeStyle(false)}>
          <div style={{ ...dotStyle('left', false) }} />
          <div style={nodeHeaderStyle(false)}>
            <div style={nodeIconBox(false)}><Users size={18} style={{ color: '#94a3b8' }} /></div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>Your Customers</div>
              <div style={{ fontSize: '0.76rem', color: '#94a3b8', marginTop: 1 }}>Get accurate answers via WhatsApp, Telegram, Web</div>
            </div>
          </div>
        </div>

      </div>}

      {/* ============ Modals ============ */}
      {showAddSheet && (
        <div className="modal-overlay" onClick={() => setShowAddSheet(false)}>
          <div className="modal" style={{ maxWidth: 580 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header"><span className="modal-title">Connect a Google Sheet</span><button className="modal-close" onClick={() => setShowAddSheet(false)}><X size={18} /></button></div>
            <div className="form-group"><label className="form-label">1. Select Spreadsheet</label>
              <select className="form-input" value={sheetForm.spreadsheetId} onChange={e => { const s = spreadsheets.find(x => x.id === e.target.value); if (s) loadTabs(s.id, s.name); }}><option value="">Choose...</option>{spreadsheets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
            {tabs.length > 0 && <div className="form-group"><label className="form-label">2. Select Tab</label><select className="form-input" value={sheetForm.sheetName} onChange={e => loadPreview(sheetForm.spreadsheetId, e.target.value)}><option value="">Choose...</option>{tabs.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}</select></div>}
            {sheetForm.sheetName && <div className="form-group"><label className="form-label">3. Purpose</label><select className="form-input" value={sheetForm.purpose} onChange={e => setSheetForm(p => ({ ...p, purpose: e.target.value }))}>{purposeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select></div>}
            {sheetForm.sheetName && <div className="form-group"><label className="form-label">4. AI Instructions (optional)</label><textarea className="form-input" rows={2} value={sheetForm.aiInstructions} onChange={e => setSheetForm(p => ({ ...p, aiInstructions: e.target.value }))} placeholder="e.g. Use this when customer asks about prices." style={{ fontSize: '0.82rem' }} /></div>}
            {preview && (
              <div style={{ marginBottom: 16 }}>
                <div className="form-label" style={{ marginBottom: 8 }}>Preview</div>
                <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                  <table style={{ fontSize: '0.78rem' }}><thead><tr>{preview.headers.map(h => <th key={h}>{h}</th>)}</tr></thead><tbody>{preview.preview.map((row, i) => <tr key={i}>{preview.headers.map(h => <td key={h}>{row[h] || '...'}</td>)}</tr>)}</tbody></table>
                </div>
                <div className="alert alert-success" style={{ marginTop: 10, marginBottom: 0 }}><CheckCircle size={13} /><span style={{ fontSize: '0.78rem' }}>Found {preview.headers.length} columns</span></div>
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}><button className="btn btn-secondary" onClick={() => setShowAddSheet(false)}>Cancel</button><button className="btn btn-primary" disabled={!sheetForm.sheetName} onClick={connectSheet}><ArrowRight size={14} /> Connect</button></div>
          </div>
        </div>
      )}
      {showUploadFile && <UploadFileModal onClose={() => setShowUploadFile(false)} onUploaded={fetchFiles} />}
      {pickedFiles.length > 0 && <DriveImportModal pickedFiles={pickedFiles} onClose={() => setPickedFiles([])} onImported={fetchFiles} />}
      {showAddDB && <DatabaseConnectModal onClose={() => setShowAddDB(false)} onConnected={fetchDatabases} />}
      {editingDb && <DatabaseConnectModal onClose={() => setEditingDb(null)} onConnected={() => { fetchDatabases(); setEditingDb(null); }} editingDb={editingDb} />}
      {showTablesDb && <DbTablesModal db={showTablesDb} onClose={() => setShowTablesDb(null)} />}

      {/* Create Workflow Modal */}
      {showCreateWorkflow && (
        <div className="modal-overlay" onClick={() => setShowCreateWorkflow(false)}>
          <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Create Workflow</span>
              <button className="modal-close" onClick={() => setShowCreateWorkflow(false)}><X size={18} /></button>
            </div>

            <div className="form-group">
              <label className="form-label">Workflow Name</label>
              <input className="form-input" placeholder="e.g. Sales Bot, Support Agent, FAQ Bot" value={workflowForm.name}
                onChange={e => setWorkflowForm(p => ({ ...p, name: e.target.value }))} />
            </div>

            <div className="form-group">
              <label className="form-label">Prompt Name</label>
              <input className="form-input" placeholder="e.g. Sales Prompt" value={workflowForm.prompt.name}
                onChange={e => setWorkflowForm(p => ({ ...p, prompt: { ...p.prompt, name: e.target.value } }))} />
            </div>

            <div className="form-group">
              <label className="form-label">System Prompt</label>
              <textarea className="form-input" rows={6} value={workflowForm.prompt.content}
                onChange={e => setWorkflowForm(p => ({ ...p, prompt: { ...p.prompt, content: e.target.value } }))}
                placeholder="You are a helpful assistant for..."
                style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', lineHeight: 1.6 }} />
            </div>

            {/* Data Sources */}
            <div className="form-group">
              <label className="form-label">Data Sources</label>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 8 }}>Select which data this workflow can access</div>

              {connectedSheets.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: 6 }}>Google Sheets</div>
                  {connectedSheets.map(s => (
                    <label key={s._id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: '0.82rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={workflowForm.dataSources.sheetIds.includes(s._id)}
                        onChange={e => {
                          const ids = e.target.checked
                            ? [...workflowForm.dataSources.sheetIds, s._id]
                            : workflowForm.dataSources.sheetIds.filter(id => id !== s._id);
                          setWorkflowForm(p => ({ ...p, dataSources: { ...p.dataSources, sheetIds: ids } }));
                        }} />
                      {s.spreadsheetName} → {s.sheetName}
                      <span className="badge badge-blue" style={{ fontSize: '0.65rem' }}>{(s.purpose || '').replace('_', ' ')}</span>
                    </label>
                  ))}
                </div>
              )}

              {files.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: 6 }}>Uploaded Files</div>
                  {files.map(f => (
                    <label key={f._id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: '0.82rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={workflowForm.dataSources.knowledgeFileIds.includes(f._id)}
                        onChange={e => {
                          const ids = e.target.checked
                            ? [...workflowForm.dataSources.knowledgeFileIds, f._id]
                            : workflowForm.dataSources.knowledgeFileIds.filter(id => id !== f._id);
                          setWorkflowForm(p => ({ ...p, dataSources: { ...p.dataSources, knowledgeFileIds: ids } }));
                        }} />
                      {f.originalName || f.name}
                      <span style={{ background: '#f1f5f9', color: '#64748b', padding: '1px 6px', borderRadius: 4, fontSize: '0.65rem', fontWeight: 600 }}>{(f.purpose || 'knowledge_base').replace(/_/g, ' ')}</span>
                    </label>
                  ))}
                </div>
              )}

              {calendarStatus?.isActive && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: '0.82rem', cursor: 'pointer' }}>
                  <input type="checkbox" checked={workflowForm.dataSources.useCalendar}
                    onChange={e => setWorkflowForm(p => ({ ...p, dataSources: { ...p.dataSources, useCalendar: e.target.checked } }))} />
                  Google Calendar
                </label>
              )}
            </div>

            {/* Assign Channels */}
            <div className="form-group">
              <label className="form-label">Assign Channels (after creation)</label>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>You can assign channels to this workflow after creating it.</div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowCreateWorkflow(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleCreateWorkflow}><Plus size={13} /> Create Workflow</button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Channel to Workflow - shown when a workflow is selected */}
      {activeWf && view === 'canvas' && (
        <div style={{
          position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', zIndex: 20,
          background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 12,
          padding: '10px 16px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
          display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.82rem',
        }}>
          <Bot size={16} style={{ color: 'var(--primary)' }} />
          <span style={{ fontWeight: 600 }}>{activeWf.name}</span>
          <span style={{ color: 'var(--text-muted)' }}>|</span>
          <select className="form-input" style={{ fontSize: '0.78rem', padding: '4px 8px', width: 'auto' }}
            value="" onChange={e => { if (e.target.value) handleAssignChannelToWorkflow(activeWf._id, e.target.value); e.target.value = ''; }}>
            <option value="">+ Assign channel...</option>
            {connectedChannels.filter(c => !c.workflowId || c.workflowId.toString() !== activeWf._id.toString()).map(c => (
              <option key={c._id} value={c._id}>{c.name} ({c.type})</option>
            ))}
          </select>
          {wfChannels.map(c => (
            <div key={c._id} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', background: 'var(--bg)', borderRadius: 6, fontSize: '0.75rem' }}>
              {c.name}
              <button onClick={() => handleRemoveChannelFromWorkflow(activeWf._id, c._id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, display: 'flex' }}>
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
